import type { ProductHeroHorizontalAlignment, ProductHeroTextColorMode } from '@graphql/generated';
import { ProductHeroGradientMode } from '@graphql/generated';
import { animationTransition } from '@shared/utils/animationTransition';
import type { StyleSystemProps } from '@withjoy/joykit';
import { Box, Flex, styled } from '@withjoy/joykit';
import type { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { pxToRem } from '@withjoy/joykit/theme';
import chroma from 'chroma-js';
import { ProductHeroVerticalAlignment } from '.';

const horizontalAlignmentConfig: Record<string, StyleSystemProps> = {
  left: { alignItems: 'flex-start', textAlign: 'start' },
  center: { alignItems: 'center', textAlign: 'center' },
  right: { alignItems: 'flex-end', textAlign: 'end' },
};

const verticalAlignmentConfig: Record<string, StyleSystemProps> = {
  center: { justifyContent: 'center' },
  bottom: { justifyContent: 'end' },
};

export const getTextColor = (textCustomColorValue?: string | null): Record<ProductHeroTextColorMode, CSSObject> => ({
  light: {
    color: 'white',
    ['& button']: {
      color: 'white',
      borderColor: 'white',
      _hover: {
        color: 'white',
        borderColor: 'white',
      },
    },
  },
  dark: {
    color: 'mono14',
    ['& button']: {
      color: 'mono14',
      borderColor: 'mono14',
      _hover: {
        color: 'mono14',
        borderColor: 'mono14',
      },
    },
  },
  custom: {
    color: textCustomColorValue,
    ['& button']: {
      color: textCustomColorValue,
      borderColor: textCustomColorValue,
      _hover: {
        color: textCustomColorValue,
        borderColor: textCustomColorValue,
      },
    },
  },
});

export const StyledWrapper = styled(Box)``;
const wrapperStyles: StyleSystemProps = {
  position: 'relative',
  minHeight: {
    _: pxToRem(530),
    sm2: 'auto',
  },
  height: {
    _: `calc(80vh - var(--mobile-public-shop-navbar-height, 0px) - var(--mobile-admin-shop-navbar-height, 0px))`,
    sm2: `54vw`,
    md: `42vw`,
  },
};

export const StyledContainer = styled(Flex)``;
const containerStyles = (horizontalAlign: ProductHeroHorizontalAlignment, verticalAlign: ProductHeroVerticalAlignment, hasChecklist?: boolean): StyleSystemProps => ({
  paddingBottom: verticalAlign === ProductHeroVerticalAlignment.bottom ? [10, null, pxToRem(80), pxToRem(80)] : 0,
  width: '100%',
  height: '100%',
  position: 'relative',
  flexDirection: 'column',
  zIndex: 1,
  ...verticalAlignmentConfig[verticalAlign],
  ...horizontalAlignmentConfig[horizontalAlign],
});

export const StyledContent = styled(Flex)``;
const contentStyles = (colorMode: ProductHeroTextColorMode, alignItems: ProductHeroHorizontalAlignment, textCustomColorValue?: string | null): StyleSystemProps => {
  return {
    ...horizontalAlignmentConfig[alignItems],
    ...getTextColor(textCustomColorValue)[colorMode],
    paddingX: [6, null, pxToRem(88), pxToRem(120)],
    flexDirection: 'column',
    zIndex: 3,
    width: '100%',
  };
};

export const StyledBlockText = styled(Flex)``;
const blockTextStyles = (alignItems: ProductHeroHorizontalAlignment, shortenTextBlock: boolean): StyleSystemProps => ({
  ...horizontalAlignmentConfig[alignItems],
  flexDirection: 'column',
  width: shortenTextBlock ? ['100%', null, pxToRem(400), pxToRem(500)] : '100%',
});

export const StyledEyebrow = styled(Box)``;
const eyebrowStyles: StyleSystemProps = {
  typographyVariant: 'hed3',
  marginBottom: 3,
};

export const StyledHed = styled(Box)``;
const hedStyles: StyleSystemProps = {
  marginBottom: 5,
  typographyVariant: {
    _: 'Hed40',
    md: 'Hed56',
  },
};

export const StyledSubtitle = styled(Box)``;
const subtitleStyles: StyleSystemProps = {
  typographyVariant: {
    _: 'body2',
    md: 'body3',
  },
};

export const StyledCheckListWrapper = styled(Flex)``;
const checkListWrapperStyles = (alignItems: ProductHeroHorizontalAlignment): StyleSystemProps => ({
  rowGap: 5,
  columnGap: 5,
  paddingTop: 3,
  paddingBottom: 5,
  flexDirection: 'column',
  ...horizontalAlignmentConfig[alignItems],
});

export const StyledCheckList = styled(Flex)``;
const checkListStyles: StyleSystemProps = {
  fontWeight: {
    _: 400,
    sm2: 600,
  },
  maxWidth: {
    _: pxToRem(255),
    sm2: '100%',
  },
  textAlign: 'left',
};

export const StyledButtonsContainer = styled(Flex)``;
const buttonsContainerStyles = (colorMode: ProductHeroTextColorMode, textCustomColorValue?: string | null, hasCtaLink?: boolean): CSSObject => ({
  ...getTextColor(textCustomColorValue)[colorMode],
  rowGap: hasCtaLink ? 6 : 5,
  columnGap: hasCtaLink ? 7 : 5,
  flexDirection: {
    _: 'column',
    sm2: 'row',
  },
  marginTop: [5, null, hasCtaLink ? 5 : 7, 7, 9],
  transition: animationTransition('color'),
  maxWidth: '100%',
  typeVariant: ['body1', null, 'Dek20', null, 'Dek24'],
  ['& button']: {
    _hover: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
});

export const StyledMediaContainer = styled(Box)``;
const mediaContainerStyles: StyleSystemProps = {
  position: 'absolute',
  top: '0px',
  left: '0px',
  right: '0px',
  zIndex: 'base',
  height: '100%',
};

const mediaLayerStyles: StyleSystemProps = {
  ...mediaContainerStyles,
  height: '100%',
  width: '100%',
};

const _gradientColor = (color?: string | null): Record<ProductHeroGradientMode, string> => ({
  custom: `${color}`,
  dark: 'black',
  light: 'white',
  none: '',
});

const _convertColorToRgba = (colorMode: ProductHeroGradientMode, alpha: number, color?: string | null) => {
  return `rgba(${chroma(_gradientColor(color)[colorMode]).alpha(alpha).rgba().join(', ')})`;
};

const gradients = (isMobile: boolean, colorMode: ProductHeroGradientMode, color?: string | null): Record<ProductHeroHorizontalAlignment, string> => ({
  left: `linear-gradient(
    -89.82deg,
    ${_convertColorToRgba(colorMode, 0, color)} 39.98102247714996%,
    ${_convertColorToRgba(colorMode, 0.38, color)} 65.1060163974762%,
    ${_convertColorToRgba(colorMode, 0.5, color)} 100%
  ),
  linear-gradient(
    270deg,
    ${_convertColorToRgba(colorMode, 0, color)} 22.25576341152191%,
    ${_convertColorToRgba(colorMode, 0.32, color)} 68.69213581085205%,
    ${_convertColorToRgba(colorMode, 0.4, color)} 100%
  )`,
  right: `linear-gradient(
    89.79deg,
    ${_convertColorToRgba(colorMode, 0, color)} 41.74487888813019%,
    ${_convertColorToRgba(colorMode, 0.38, color)} 92.70833134651184%,
    ${_convertColorToRgba(colorMode, 0.5, color)} 100%
  ),
  linear-gradient(
    90.07deg,
    ${_convertColorToRgba(colorMode, 0, color)} 0%,
    ${_convertColorToRgba(colorMode, 0.32, color)} 68.69213581085205%,
    ${_convertColorToRgba(colorMode, 0.4, color)} 100%
  )`,
  center: isMobile
    ? `linear-gradient(
      180deg,
      ${_convertColorToRgba(colorMode, 0, color)} 41.74487888813019%,
      ${_convertColorToRgba(colorMode, 0.45, color)} 92.70833134651184%,
      ${_convertColorToRgba(colorMode, 0.6, color)} 100%
    ),
    linear-gradient(
      180deg,
      ${_convertColorToRgba(colorMode, 0, color)} 0%,
      ${_convertColorToRgba(colorMode, 0.4, color)} 68.69213581085205%,
      ${_convertColorToRgba(colorMode, 0.5, color)} 100%
    )`
    : `linear-gradient(
      180deg,
      ${_convertColorToRgba(colorMode, 0, color)} 41.74487888813019%,
      ${_convertColorToRgba(colorMode, 0.38, color)} 92.70833134651184%,
      ${_convertColorToRgba(colorMode, 0.5, color)} 100%
    ),
    linear-gradient(
      180deg,
      ${_convertColorToRgba(colorMode, 0, color)} 0%,
      ${_convertColorToRgba(colorMode, 0.32, color)} 68.69213581085205%,
      ${_convertColorToRgba(colorMode, 0.4, color)} 100%
    )`,
});

const overlayGradient = (colorMode: ProductHeroGradientMode, color?: string | null): string =>
  `linear-gradient(to bottom, ${_convertColorToRgba(colorMode, 0.6, color)}, ${_convertColorToRgba(colorMode, 0.6, color)})`;

export const StyledGradient = styled(Box)``;
const gradientStyles = (
  isMobile: boolean,
  alignment: ProductHeroHorizontalAlignment,
  gradientMode: ProductHeroGradientMode,
  gradientCustomColorValue?: string | null,
  isOverlayGradient?: boolean,
): StyleSystemProps => ({
  ...mediaLayerStyles,
  zIndex: 3,
  backgroundImage:
    gradientMode === ProductHeroGradientMode.none
      ? undefined
      : isOverlayGradient
        ? overlayGradient(gradientMode, gradientCustomColorValue)
        : gradients(isMobile, gradientMode, gradientCustomColorValue)[alignment],
});

export const StyledBackgroundVideo = styled(Box)``;
const backgroundVideoStyles = ($backgroundImageUrl: Maybe<string>): StyleSystemProps => ({
  ...mediaLayerStyles,
  backgroundImage: $backgroundImageUrl ? `url("${$backgroundImageUrl}")` : undefined,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  zIndex: 2,
});

export const StyledBackgroundVideoContainer = styled(Box)``;
const backgroundVideoContainerStyles: CSSObject = {
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
  padding: '0',
  ['& iframe']: {
    left: '50%',
    minHeight: '100%',
    minWidth: {
      _: '100%',
      sm2: '130%',
    },
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
    top: '50%',
    height: '178vh',
  },
};

export const StyledBackgroundImage = styled(Box)``;
const backgroundImageStyles = ($backgroundImageUrl: string): StyleSystemProps => ({
  ...mediaLayerStyles,
  zIndex: 1,
  backgroundImage: `url("${$backgroundImageUrl}")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
});

export const styles = {
  wrapper: wrapperStyles,
  container: containerStyles,
  content: contentStyles,
  blockText: blockTextStyles,
  eyebrow: eyebrowStyles,
  hed: hedStyles,
  subtitle: subtitleStyles,
  buttonsContainer: buttonsContainerStyles,
  mediaContainer: mediaContainerStyles,
  gradient: gradientStyles,
  backgroundVideo: backgroundVideoStyles,
  backgroundVideoContainer: backgroundVideoContainerStyles,
  backgroundImage: backgroundImageStyles,
  checkListWrapper: checkListWrapperStyles,
  checkList: checkListStyles,
};
